import { Card } from '@aws-amplify/ui-react';
import { FC } from 'react';
import { BarLoader } from 'react-spinners';
import { InfoBar } from '@/components/ui/info-bar';
import { SectionHeadingGroup } from '@/components/ui/section-heading-group';
import { useUnitAddressBooks } from '@/hooks/use-address-books';
import { useCurrentUnit } from '@/hooks/use-current-unit';
import { usePolicies } from '@/hooks/use-policies';
import { SectionContainerLayout } from '@/layouts/section-container-layout';
import { AddAddressBookForm } from './add-address-book-form';
import { computeAddressBookDrift } from './address-book-drift';
import { groupDevicesByAddressBook } from './address-book-grouping';
import { AddressBookSummary } from './address-book-summary';
import { CreateAddressBookForm } from './create-address-book-form';
import { DeviceAddressBookCard } from './device-address-book-card';

export const CurrentAddressBooksView: FC = () => {
  const { data: devices, isFetching, isError } = useUnitAddressBooks();
  const { data: unit } = useCurrentUnit();
  const { data: policies } = usePolicies();

  const editEnabled = policies?.addressBook?.editEnabled ?? false;
  const organisationId = unit?.organisation.id ?? -1;
  const unitId = unit?.id ?? -1;
  const { driftDetected } = computeAddressBookDrift(devices);

  const devicesWithBooks = devices.filter(
    (device) => device.addressBooks.length > 0,
  );
  const devicesWithoutBooks = devices.filter(
    (device) => device.addressBooks.length === 0,
  );
  const addressBookGroups = groupDevicesByAddressBook(devicesWithBooks);

  return (
    <>
      <SectionContainerLayout horizontal className="border-b-0">
        <SectionHeadingGroup
          className="mb-6"
          subtitle="Verify each Alexa device in this unit has the correct address book"
          title="Current Address Books"
        />
        <div className="flex grow flex-col gap-4">
          {isFetching ? (
            <BarLoader color="gray" />
          ) : isError ? (
            <InfoBar
              text="Couldn't load address books for this unit. Please try again."
              variant="error"
            />
          ) : devices.length === 0 ? (
            <InfoBar text="No Alexa devices in this unit." variant="error" />
          ) : driftDetected ? (
            <>
              <InfoBar
                text="Devices in this unit are using different address books. They should normally share the same Friends & Family book."
                variant="error"
              />
              {devices.map((device) => (
                <DeviceAddressBookCard
                  device={device}
                  driftDetected={driftDetected}
                  editEnabled={editEnabled}
                  key={device.deviceId}
                  organisationId={organisationId}
                  unitId={unitId}
                />
              ))}
            </>
          ) : (
            <>
              {addressBookGroups.map((group) => (
                <AddressBookSummary
                  addressBook={group.addressBook}
                  devices={group.devices}
                  editEnabled={editEnabled}
                  key={group.addressBook.addressBookId}
                  organisationId={organisationId}
                  unitId={unitId}
                />
              ))}
              {devicesWithoutBooks.map((device) => (
                <DeviceAddressBookCard
                  device={device}
                  driftDetected={false}
                  editEnabled={editEnabled}
                  key={device.deviceId}
                  organisationId={organisationId}
                  unitId={unitId}
                />
              ))}
            </>
          )}
        </div>
      </SectionContainerLayout>
      {editEnabled === true && (
        <SectionContainerLayout horizontal className="border-b-0">
          <SectionHeadingGroup
            subtitle="Create address books or add an existing one"
            title="Add Address Book"
          />
          <Card className={'flex flex-col gap-4'}>
            <AddAddressBookForm />
            <CreateAddressBookForm />
          </Card>
        </SectionContainerLayout>
      )}
    </>
  );
};
